import { useCallback, useState } from 'react';
import { MENTION_ALL, MentionCandidate } from '../helpers/chatMentions';

/**
 * A row in the `@` menu: a member, or the one row that names them all.
 * The `all` row carries no id - nothing about it is a person.
 */
export type MentionOption =
  | { kind: 'member'; candidate: MentionCandidate }
  | { kind: 'all' };

/** What the `all` row answers to besides its own name. */
const ALL_KEYWORDS = [MENTION_ALL, 'everyone'];

/** Names past this are a list, not a menu; the search is what narrows it. */
const MAX_MATCHES = 6;

/**
 * How many words after the `@` still count as one search.
 *
 * A space cannot end the query, because the names being searched contain them:
 * stopping at the first one meant "@Milan B" closed the menu and left you
 * typing the rest of a colleague's name by hand. Three words is where a
 * search stops being a search - it is a full name plus room to be wrong about
 * it - and past that the menu closes rather than following you into a
 * sentence.
 */
const MAX_QUERY_WORDS = 3;

/**
 * The `@` currently being typed, if any.
 *
 * Only matches when the `@` starts a word: mid-word it is an email address,
 * not a mention.
 */
const MENTION_QUERY = new RegExp(
  // The whole run is optional so a bare "@" opens the full list, but a word has
  // to come first for the space to count - otherwise "email me @ home" reads as
  // a search for everyone whose name has a space in it.
  `(?:^|\\s)@((?:[^\\s@]+(?: [^\\s@]*){0,${MAX_QUERY_WORDS - 1}})?)$`
);

const activeMentionQuery = (value: string, caret: number) => {
  const match = MENTION_QUERY.exec(value.slice(0, caret));
  return match ? match[1] : null;
};

type Options = {
  /**
   * Who can be mentioned. Empty or absent turns the menu off, which is what a
   * support thread wants - there is nobody to mention there.
   */
  candidates?: MentionCandidate[];
  /** Offers `@all` above the members - groups only. */
  withAll?: boolean;
  /** The draft, and how to replace it when a name is picked. */
  value: string;
  onChange: (next: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
};

/**
 * The `@` menu's state machine: what is being typed, who matches, which one is
 * highlighted, and inserting the one that is chosen.
 *
 * Kept out of the composer because it owns keys the composer also wants - Enter
 * picks a name here and must not also post the message - so the two only meet
 * at `onKeyDown` below, which returns whether it handled the key.
 */
export const useMentions = ({
  candidates,
  withAll,
  value,
  onChange,
  textareaRef,
}: Options) => {
  const [query, setQuery] = useState<string | null>(null);
  const [index, setIndex] = useState(0);

  const enabled = !!candidates?.length;

  const search = query?.toLowerCase() ?? '';
  const offerAll =
    withAll && ALL_KEYWORDS.some((keyword) => keyword.startsWith(search));

  const matches: MentionOption[] =
    query === null || !enabled
      ? []
      : [
          // First, as the one row nobody has to scroll for.
          ...(offerAll ? [{ kind: 'all' as const }] : []),
          ...candidates!
            .filter((candidate) =>
              candidate.name.toLowerCase().includes(search)
            )
            .slice(0, MAX_MATCHES)
            .map((candidate) => ({ kind: 'member' as const, candidate })),
        ];

  const close = useCallback(() => {
    setQuery(null);
    setIndex(0);
  }, []);

  /** Replace the half-typed `@ana` under the caret with the full handle. */
  const apply = useCallback(
    (option: MentionOption) => {
      const name = option.kind === 'all' ? MENTION_ALL : option.candidate.name;
      const element = textareaRef.current;
      const caret = element?.selectionStart ?? value.length;
      const typed = value.slice(0, caret);
      const after = value.slice(caret);

      /* Cut exactly what the menu was searching on, measured from the same
         match rather than by a second regex - a half-typed "@Milan B" is two
         words, and a pattern that only ever ate one left the first behind and
         wrote the full name after it. */
      const query = MENTION_QUERY.exec(typed);
      const before = query
        ? typed.slice(0, typed.length - query[1].length - 1)
        : typed;

      onChange(`${before}@${name} ${after}`);
      close();

      // Put the caret after the inserted handle rather than at the end - the
      // draft may well continue past it.
      const position = before.length + name.length + 2;
      window.requestAnimationFrame(() => {
        element?.focus();
        element?.setSelectionRange(position, position);
      });
    },
    [value, onChange, close, textareaRef]
  );

  /** Called on every keystroke, to open or narrow the menu. */
  const trackCaret = useCallback(
    (next: string, caret: number) => {
      if (!enabled) return;
      setQuery(activeMentionQuery(next, caret));
      setIndex(0);
    },
    [enabled]
  );

  /**
   * True when the menu consumed the key, which is the composer's signal to do
   * nothing else with it.
   */
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (matches.length === 0) return false;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setIndex((current) => (current + 1) % matches.length);
        return true;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setIndex((current) => (current - 1 + matches.length) % matches.length);
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        apply(matches[index]);
        return true;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return true;
      }
      return false;
    },
    [matches, index, apply, close]
  );

  return { matches, index, apply, close, trackCaret, onKeyDown };
};
